import { Box, Button, Stack } from '@mui/material'
import styles from './Login.module.scss'
import classNames from 'classnames/bind'
import { EFPasswordField, EFTextField, EFToastError, EFTypography, Spinner } from '~/components'
import { useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useLoginUserMutation } from '~/redux/services/authApi'
import { ALL_NS } from '~/i18n/namespaces'
import { useTranslation } from 'react-i18next'
import { getErrorMessage } from '~/redux/services/apiServices'
import { ILoginForm } from '~/models'
import { LoginService } from './Login.service'

const cx = classNames.bind(styles)

export const AuthenticationEvents = new EventTarget()
export const LoginSuccessEventKey = 'AuthenticationSucceeded'

export default function LoginPage() {
  const { t } = useTranslation(ALL_NS)

  const { ssoApiURLService } = LoginService(window.location.pathname, window.location.search)

  const methods = useForm<ILoginForm>()
  const {
    reset,
    handleSubmit,
    formState: { isSubmitSuccessful, errors },
    register
  } = methods

  const [loginUser, { isLoading, isError, error, isSuccess }] = useLoginUserMutation()
  const { isFetching: isFetchingSsoUrl, error: ssoError, isSuccess: ssoSuccess, data: ssoData } = ssoApiURLService

  const onSubmitHandler: SubmitHandler<ILoginForm> = values => {
    // 👇 Executing the loginUser Mutation
    loginUser(values)
    reset(values)
  }

  useEffect(() => {
    if (isSuccess) {
      AuthenticationEvents.dispatchEvent(new CustomEvent(LoginSuccessEventKey))
    }
  }, [isSuccess])

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful])

  useEffect(() => {
    document.title = t('PageTitlePostfix_tk0_TaitEnableFleet', {
      0: t('PageTitle_Login')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack className={cx('login-wrapper')}>
      <h1 className={cx('login-title')}>{t('PageTitle_Login')}</h1>
      <FormProvider {...methods}>
        <Box component='form' onSubmit={handleSubmit(onSubmitHandler)} display='flex' flexDirection='column'>
          <EFTextField
            label={`${t('Label_NodeUserName')} *`}
            placeholder={t('Label_NodeUserName')}
            type='text'
            sx={{ '& *': { color: 'var(--dark) !important' } }}
            {...register('userName', {
              required: t('Message_RequiredField_tk0_', { 0: 'Username' })
            })}
            autocompletehint='username'
            helperText={errors.userName?.message}
            error={errors?.userName && errors.userName.message !== ''}
          />

          <EFPasswordField
            label={`${t('Label_NodePassword')} *`}
            placeholder={t('Label_NodePassword')}
            sx={{ '& *': { color: 'var(--dark) !important' } }}
            {...register('password', {
              required: t('Message_RequiredField_tk0_', { 0: 'Password' })
            })}
            helperText={errors.password?.message}
            error={errors?.password && errors.password.message !== ''}
            autocompletehint='current-password'
          />
          {isError && (
            <Stack py={0.5} my={1}>
              <EFToastError text={t(getErrorMessage(error as any))} />
            </Stack>
          )}

          <div className='login-method'>
            <Button id='signin-btn' type='submit' variant='contained' className='primary-button' disabled={isLoading} sx={{ width: '100%' }}>
              {isLoading && <Spinner />}
              <EFTypography ml={1} fontWeight={500} fontSize='var(--fs-lg) !important'>
                {t('Button_Login')}
              </EFTypography>
            </Button>
          </div>
          <div className='login-method'>
            {ssoData?.configured &&
              ssoData.url && ( // Render the signinsso-btn only if sso server is configured
                <Button
                  id='signinsso-btn'
                  type='button'
                  variant='contained'
                  className='primary-button'
                  sx={{ width: '100%', fontSize: 'var(--fs-lg) !important' }}
                  onClick={() => (window.location.href = ssoData.url!)}
                >
                  {isFetchingSsoUrl && <Spinner />}
                  <EFTypography ml={1} fontWeight={500} fontSize='var(--fs-lg) !important'>
                    {t('Button_SSOLogin')}
                  </EFTypography>
                </Button>
              )}

            {ssoData?.configured &&
              ssoData?.message && ( // Render the signinsso-btn only if sso server is configured
                <div className='toast-error'>{ssoData.message}</div>
              )}

            {!ssoSuccess &&
              Boolean(ssoError) && ( // Render the signinsso-btn only if sso server is configured
                <div className='toast-error'>{t('Warning_UnableToGenerateSsoUrl')}</div>
              )}
          </div>
        </Box>
      </FormProvider>
      <div></div>
    </Stack>
  )
}
